/**
 * components/SplashScreen.tsx
 * ------------------------------------------------------------------
 * Branded launch splash. Continues the instant splash painted in
 * index.html (#lkc-splash) so there is one seamless brand moment from
 * first paint through to the login form:
 *
 *   1. index.html paints #lkc-splash immediately (before any JS).
 *   2. React mounts; this component removes the static #lkc-splash and
 *      renders an identical overlay it controls.
 *   3. It holds for a minimum time (so fast loads don't flash), then
 *      fades out once `ready` is true, revealing whatever is beneath
 *      (the login form or the app).
 *
 * Visuals mirror the login brand panel (Login.tsx): the same navy
 * gradient (#0f1b30 -> #1b3a6b -> #142c52), the school crest, and a
 * gold (#d4a92c) indeterminate progress bar.
 *
 * Usage (in App.tsx Gate, above the loading branch):
 *   const [appReady, setAppReady] = useState(false);
 *   // set true once auth has resolved (see App.tsx wiring)
 *   <SplashScreen ready={appReady} />
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import SchoolLogo from '@/components/ui/SchoolLogo';

const MIN_DISPLAY_MS = 1000; // hold at least this long after mount
const FADE_MS = 400;

interface Props {
  /** Becomes true when the app is ready to show what's beneath. */
  ready: boolean;
}

export default function SplashScreen({ ready }: Props) {
  const [mountedAt] = useState(() => Date.now());
  const [hide, setHide] = useState(false);   // start fade
  const [gone, setGone] = useState(false);   // unmount after fade

  // Remove the static index.html splash as soon as React's splash is up,
  // so the two never double-render.
  useEffect(() => {
    const el = document.getElementById('lkc-splash');
    if (el) el.parentNode?.removeChild(el);
  }, []);

  // Once ready, wait out the remaining minimum-display time, then fade.
  useEffect(() => {
    if (!ready) return;
    const elapsed = Date.now() - mountedAt;
    const wait = Math.max(0, MIN_DISPLAY_MS - elapsed);
    const t1 = setTimeout(() => setHide(true), wait);
    const t2 = setTimeout(() => setGone(true), wait + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [ready, mountedAt]);

  if (gone) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        background:
          'radial-gradient(120% 120% at 75% 15%, rgba(212,169,44,0.08), transparent 55%),' +
          'radial-gradient(120% 120% at 10% 110%, rgba(45,82,149,0.18), transparent 55%),' +
          'linear-gradient(135deg, #0f1b30 0%, #1b3a6b 55%, #142c52 100%)',
        opacity: hide ? 0 : 1,
        pointerEvents: hide ? 'none' : 'auto',
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <div
        style={{
          filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
          animation: 'lkc-splash-pop 600ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <SchoolLogo size={96} />
      </div>

      <div
        role="progressbar"
        aria-label="Loading"
        style={{
          width: 180,
          height: 4,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '40%',
            borderRadius: 999,
            background:
              'linear-gradient(90deg, transparent, #d4a92c, transparent)',
            animation: 'lkc-splash-slide 1100ms ease-in-out infinite',
          }}
        />
      </div>

      {/* Keyframes are also defined in index.html for the static splash;
          re-declared here so the component is self-contained if reused. */}
      <style>{`
        @keyframes lkc-splash-slide {
          0%   { left: -40%; }
          100% { left: 100%; }
        }
        @keyframes lkc-splash-pop {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
